
import { useCallback, useEffect, useState } from "react";
import { supabase } from "../supabaseClient";

let globalHasBusiness = null;
const subscribers = new Set();

const BUSINESS_REWARD_KEY = "profile_gate_business_complete";

const NO_BUSINESS_OPTION =
  "I don't have a business but I want to start one the right way";

// Legacy compatibility for users who completed onboarding
// before onboarding_completed_at was introduced.
function hasCompleteLegacyBusinessProfile(row) {
  if (!row) return false;

  const businessName = String(row.business_name || "").trim();
  const legalNature = String(row.legal_nature || "").trim();
  const registrationNumber = String(row.registration_number || "").trim();
  const industry = String(row.industry || "").trim();
  const monthlyRevenueRange = String(
    row.monthly_revenue_range || ""
  ).trim();
  const businessAge = String(row.business_age || "").trim();
  const address = String(row.address || "").trim();
  const province = String(row.province || "").trim();

  const needsRegistration = [
    "Close Corporation",
    "Company",
    "Non-Profit Organisation",
  ].includes(legalNature);

  if (!businessName) return false;
  if (!legalNature) return false;
  if (!industry) return false;
  if (!monthlyRevenueRange) return false;
  if (!businessAge) return false;
  if (!address) return false;
  if (!province) return false;
  if (needsRegistration && !registrationNumber) return false;

  return true;
}

async function hasBusinessCompletionAward(userId) {
  if (!userId) return false;

  const { data, error } = await supabase
    .from("user_achievements")
    .select("achievement_key")
    .eq("user_id", userId)
    .eq("achievement_key", BUSINESS_REWARD_KEY)
    .maybeSingle();

  if (error && error.code !== "PGRST116") {
    throw error;
  }

  return data?.achievement_key === BUSINESS_REWARD_KEY;
}

// Primary access check:
// 1. New onboarding completion timestamp
// 2. Legacy completion achievement
// 3. Legacy completed business profile
export async function getHasBusinessForUser(userId) {
  if (!userId) return false;

  const {
    data: onboardingRow,
    error: onboardingError,
  } = await supabase
    .from("user_onboarding_profile")
    .select("onboarding_completed_at")
    .eq("user_id", userId)
    .maybeSingle();

  if (onboardingError && onboardingError.code !== "PGRST116") {
    throw onboardingError;
  }

  if (onboardingRow?.onboarding_completed_at) {
    return true;
  }

  const legacyAward = await hasBusinessCompletionAward(userId);

  if (legacyAward) {
    return true;
  }

  const {
    data: businessRow,
    error: businessError,
  } = await supabase
    .from("business_profiles")
    .select(
      [
        "app_reason",
        "business_name",
        "legal_nature",
        "registration_number",
        "industry",
        "monthly_revenue_range",
        "business_age",
        "address",
        "province",
      ].join(", ")
    )
    .eq("user_id", userId)
    .maybeSingle();

  if (businessError && businessError.code !== "PGRST116") {
    throw businessError;
  }

  if (!businessRow) {
    return false;
  }

  const appReason = String(businessRow.app_reason || "").trim();

  if (appReason === NO_BUSINESS_OPTION) {
    return false;
  }

  return hasCompleteLegacyBusinessProfile(businessRow);
}

// A signal causes subscribers to re-check Supabase.
// It does not automatically grant onboarding access.
export function signalBusinessProfileUpdated() {
  globalHasBusiness = null;

  subscribers.forEach((refreshSubscriber) => {
    try {
      refreshSubscriber();
    } catch {
      // A subscriber failure must not stop other subscribers.
    }
  });
}

export function useBusinessGate() {
  const [hasBusiness, setHasBusiness] = useState(
    globalHasBusiness === true
  );

  const [loading, setLoading] = useState(
    globalHasBusiness === null
  );

  const loadGate = useCallback(async () => {
    try {
      setLoading(true);

      const { data, error } = await supabase.auth.getUser();

      if (error) {
        throw error;
      }

      const uid = data?.user?.id;

      if (!uid) {
        globalHasBusiness = false;
        setHasBusiness(false);
        setLoading(false);
        return false;
      }

      const has = await getHasBusinessForUser(uid);

      globalHasBusiness = has;
      setHasBusiness(has);
      setLoading(false);

      return has;
    } catch (error) {
      console.warn("useBusinessGate error:", error);

      globalHasBusiness = false;
      setHasBusiness(false);
      setLoading(false);

      return false;
    }
  }, []);

  useEffect(() => {
    let mounted = true;

    const run = async () => {
      if (!mounted) return;
      await loadGate();
    };

    run();

    const subscriber = () => {
      if (!mounted) return;
      run();
    };

    subscribers.add(subscriber);

    return () => {
      mounted = false;
      subscribers.delete(subscriber);
    };
  }, [loadGate]);

  const refresh = useCallback(async () => {
    globalHasBusiness = null;
    return loadGate();
  }, [loadGate]);

  return {
    hasBusiness,
    loading,
    refresh,

    // Retained for compatibility with older callers.
    // Personal details are now handled at registration.
    needsPhone: false,
  };
}
